import { useCallback, useEffect, useRef, useState } from 'react';
import { ref, query, orderByKey, limitToLast, endBefore, onValue, get } from 'firebase/database';
import { db } from '../../../integrations/firebase';
import {
    PAGE_SIZE, MAX_SEEK_PAGES, SEEK_PAGE_SIZE,
    entriesOf, applyLiveWindow, applyOlderPage, toList, oldestKey,
} from '../logic/messageWindow';

/**
 * A conversation's messages: the newest PAGE_SIZE live, older ones on request.
 *
 * Replaces an unbounded `onValue(conversations/{id}/messages)` that downloaded — and kept
 * live — the entire history of every open conversation. See logic/messageWindow.js.
 *
 * @returns {{
 *   messages: object[], loading: boolean, error: string|null,
 *   hasOlder: boolean, loadingOlder: boolean,
 *   loadOlder: () => Promise<number>,
 *   seek: (messageId: string) => Promise<boolean>,
 * }}
 */
export function useMessageWindow(chatId) {
    const heldRef = useRef(new Map());
    const chatRef = useRef(chatId);
    const busyRef = useRef(false);
    const reachedStartRef = useRef(false);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasOlder, setHasOlder] = useState(false);
    const [loadingOlder, setLoadingOlder] = useState(false);
    // The chat id whose first live snapshot has arrived. `loading` alone is not enough:
    // on the render right after a chat switch it is still false from the previous chat,
    // and a jump-to-message fired then searched an empty window and reported "not found".
    const [readyFor, setReadyFor] = useState(null);

    const publish = useCallback((held) => {
        heldRef.current = held;
        setMessages(toList(held));
    }, []);

    useEffect(() => {
        chatRef.current = chatId;
        heldRef.current = new Map();
        busyRef.current = false;
        reachedStartRef.current = false;
        setMessages([]);
        setError(null);
        setHasOlder(false);
        setLoadingOlder(false);
        if (!chatId) { setLoading(false); return undefined; }

        setLoading(true);
        const live = query(ref(db, `conversations/${chatId}/messages`), orderByKey(), limitToLast(PAGE_SIZE));
        const unsubscribe = onValue(live, (snap) => {
            if (chatRef.current !== chatId) return;        // a late event from the previous chat
            const { held, complete } = applyLiveWindow(heldRef.current, entriesOf(snap.val()), PAGE_SIZE);
            publish(held);
            // Once paging has reached the first message, a new message arriving must not
            // bring the "load older" control back.
            setHasOlder(!complete && !reachedStartRef.current);
            setLoading(false);
            setReadyFor(chatId);
        }, (err) => {
            if (chatRef.current !== chatId) return;
            // Typically PERMISSION_DENIED: removed from the group, or not a party to this chat.
            setError(/permission/i.test(err?.message || '') ? 'You no longer have access to this conversation.' : 'Messages could not be loaded.');
            publish(new Map());
            setLoading(false);
        });
        return () => unsubscribe();
    }, [chatId, publish]);

    /** Fetch one page older than what is held. Resolves to the number of messages added. */
    const fetchOlder = useCallback(async (pageSize) => {
        const id = chatRef.current;
        const cursor = oldestKey(heldRef.current);
        if (!id || !cursor) return { added: 0, complete: true };
        const snap = await get(query(ref(db, `conversations/${id}/messages`), orderByKey(), endBefore(cursor), limitToLast(pageSize)));
        if (chatRef.current !== id) return { added: 0, complete: true };   // switched chats mid-flight
        const page = entriesOf(snap.val());
        const before = heldRef.current.size;
        const { held, complete } = applyOlderPage(heldRef.current, page, pageSize);
        publish(held);
        if (complete) reachedStartRef.current = true;
        setHasOlder(!complete);
        return { added: held.size - before, complete };
    }, [publish]);

    const loadOlder = useCallback(async () => {
        if (busyRef.current) return 0;
        busyRef.current = true;
        setLoadingOlder(true);
        try {
            return (await fetchOlder(PAGE_SIZE)).added;
        } catch (e) {
            console.warn('[chat] loading older messages failed:', e?.message);
            return 0;
        } finally {
            busyRef.current = false;
            setLoadingOlder(false);
        }
    }, [fetchOlder]);

    /**
     * Make sure `messageId` is loaded, paging back as far as needed (bounded). Used to jump
     * to a pinned message or a search hit that is older than the live window — previously
     * the jump silently did nothing because the element was not on the page.
     */
    const seek = useCallback(async (messageId) => {
        if (!messageId) return false;
        if (heldRef.current.has(messageId)) return true;
        if (busyRef.current) return false;
        busyRef.current = true;
        setLoadingOlder(true);
        try {
            for (let i = 0; i < MAX_SEEK_PAGES; i++) {
                const cursor = oldestKey(heldRef.current);
                if (cursor && messageId > cursor) return false;    // inside our range but absent: it does not exist
                const { complete } = await fetchOlder(SEEK_PAGE_SIZE);
                if (heldRef.current.has(messageId)) return true;
                if (complete) return false;
            }
            return false;
        } catch (e) {
            console.warn('[chat] seeking message failed:', e?.message);
            return false;
        } finally {
            busyRef.current = false;
            setLoadingOlder(false);
        }
    }, [fetchOlder]);

    return { messages, loading, ready: !!chatId && readyFor === chatId, error, hasOlder, loadingOlder, loadOlder, seek };
}
